import React from "react";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
  FaVimeo,
  FaPinterest,
  FaTiktok,
} from "react-icons/fa";
import { IMediaItem } from "../../lib/Media";
import { ESocialChannel, IOffice, ISocialLink } from "../../lib/Contact";
import { ILinkFunc } from "../../lib/util";
import { IFooterProps } from "./util";
import SegmentConsentManager from "../Utility/SegmentConsentManager"
import { openConsentManager } from "@segment/consent-manager"
import { BsToggles } from "react-icons/bs";

export const Footer: React.FC<IFooterProps> = ({
  className = "",
  logo,
  offices,
  htmlCols,
  socialMediaLinks,
  utilsLinks,
  bottomTextLines,
  linkFunc,
  showAttribution,
  segmentWriteKey,
  segmentConsentManagerProps,
  hideFooter = false,
}) => {
  if(hideFooter) {
    return (<></>)
  }

  const LinkF = linkFunc || ((props) => <a {...props} />);
  let colsInd = 0;
  return (
    <footer
      className={`relative flex flex-col items-center w-full ${className}`}
    >
      <div className="flex flex-col items-center w-full max-w-screen-xl px-4 lg:px-16 xl:px-16 pt-14 pb-8">
        <div className="flex flex-row flex-wrap lg:flex-nowrap md:justify-start lg:justify-between items-start w-full">
          {logo && (
            <LinkF
              href="/"
              className="w-full lg:w-1/5 pb-9 lg:pb-0 xl:pb-0 xl:pl-10"
            >
              <>
                <span className="sr-only">Homepage</span>
                <img
                  className="w-2/3 md:w-1/3 lg:w-3/4 xl:w-full"
                  src={logo.url}
                  width={logo.dimensions?.[0]}
                  height={logo.dimensions?.[1]}
                  alt={logo.caption}
                />
              </>
            </LinkF>
          )}
          {htmlCols &&
            htmlCols.map((html: string, index: number) => (
              <div
                className={`html-col not-italic w-1/2 md:w-1/4 lg:w-auto lg:max-w-[20%] xl:max-w-[15%] text-sm ${className}`}
                dangerouslySetInnerHTML={{ __html: html }}
                key={colsInd++}
              />
            ))}

          {offices &&
            offices.map((item: IOffice, i: number) => (
              <address
                key={colsInd++}
                className={`not-italic w-1/2 md:w-1/4 lg:w-auto lg:max-w-[20%] xl:max-w-[15%] office-address`}
              >
                <div className="block font-bold uppercase text-sm leading-none w-full pb-1">
                  {item.fullName}
                </div>
                <div className="text-xs leading-none w-full">
                  <div className="pb-1">{item.address?.street1}</div>
                  <div className="pb-1">
                    {item.address?.city}, {item.address?.state}{" "}
                    {item.address?.postal}
                  </div>
                  {item.phone && (
                    <div>
                      Phone: <a href={`tel:${item.phone}`}>{item.phone}</a>
                    </div>
                  )}
                </div>
              </address>
            ))}

          {(socialMediaLinks || utilsLinks || bottomTextLines) && (
            <div
              className={`flex flex-col w-full lg:w-2/5 xl:w-1/3 pt-9 lg:pt-0 links-column`}
              key={colsInd++}
            >
              <div className="flex flex-row flex-wrap w-full leading-none pb-2">
                {socialMediaLinks &&
                  socialMediaLinks.map((item, i) => (
                    <a
                      key={i}
                      className="p-3"
                      href={`${item.url}`}
                      title={item.text}
                      target={item.target}
                    >
                      {(() => {
                        switch (item.channel) {
                          case ESocialChannel.Facebook:
                            return <FaFacebookF />;
                          case ESocialChannel.Twitter:
                            return <FaTwitter />;
                          case ESocialChannel.Instagram:
                            return <FaInstagram />;
                          case ESocialChannel.YouTube:
                            return <FaYoutube />;
                          case ESocialChannel.LinkedIn:
                            return <FaLinkedinIn />;
                          case ESocialChannel.Vimeo:
                            return <FaVimeo />;
                          case ESocialChannel.Pinterest:
                            return <FaPinterest />;
                          case ESocialChannel.TikTok:
                            return <FaTiktok />;
                          default:
                            return null;
                        }
                      })()}
                    </a>
                  ))}
              </div>

              {utilsLinks && (
                <div className="flex flex-row flex-wrap w-full text-xs leading-none pb-2">
                  {utilsLinks.map((item, i) => (
                    <LinkF
                      className="uppercase mr-3 underline"
                      key={i}
                      href={`${item.url}`}
                      target={item.target}
                    >
                      {item.text}
                    </LinkF>
                  ))}
                </div>
              )}

              {bottomTextLines && (
                <div className="flex flex-col w-full pt-6 lg:pt-0 text-xs leading-none">
                  {bottomTextLines.map((item: string, i: number) => (
                    <div key={i} className="pb-1" dangerouslySetInnerHTML={{ __html: item }}>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {showAttribution && (
          <div className="attribution text-xs text-center pt-7 lg:pt-16">
            Real estate website design, development and optimization by{" "}
            <a href="https://www.organicreturn.com/" className="font-bold">
              Organic Return
            </a>
          </div>
        )}
        {segmentWriteKey && segmentWriteKey.length > 0 && (
          <a className="cursor-pointer text-xs flex gap-x-1" onClick={openConsentManager}>
            <BsToggles className="text-body-text" /> Website Data Collection Preferences
          </a>
        )}
        {segmentWriteKey && segmentWriteKey.length > 0 && (
          <SegmentConsentManager segmentWriteKey={segmentWriteKey} {...segmentConsentManagerProps} />
        )}
      </div>
    </footer>
  );
};

export default Footer;
